import { SiloAsset, getBorrowableAssets } from 'app/queries/decoratedSilo';
import { useQueryInterestRateModelConfig } from 'app/queries/siloAssetInterestRateModel';
import { flow } from 'fp-ts/lib/function';
import { useEffect } from 'react';
import { useMemo, useState } from 'react';
import { useThisDecoratedSilo } from '../silo';
import { UtilizationChart } from './UtilizationChart';
import { useQueryGraphInterestRates } from 'app/queries/interest-rate/graph';
import { ApyChart } from './ApyChart';
import { ChartDateRange, ChartRangeToggle, isHourlyChartDateRange } from './ChartRangeToggle';
import {
  useChartDataForDayRangeAndAsset,
  useInterestRatesChartData,
} from 'app/queries/interest-rate/interest-rates-chart-data';
import { LegendItem } from './LegendItems';
import { ApyChartLegend } from './ApyChartLegend';
import { calculateTrends } from './apy-chart-trend';
import { ActionPanels } from '../common/ActionForm/ActionForm';
import { useSiloConfiguration } from '@silo/config-legacy';
import { ChecksumAddress } from '@silo/types';
import { getIrmMeta } from './utils';
import { asPrecisePct, asWholePct, normalizeValue } from '@silo/utils';
import { useSiloWeb3 } from '@silo/web3';
import { ToggleGroup } from '@silo/ui';

const toChartValue = flow(normalizeValue, (value) => value * 100);

export const ChartsPanel = () => {
  const silo = useThisDecoratedSilo();
  const { protocolInstance } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);
  const siloConfig = getConfigurationForSilo(silo.siloAddress);
  const assets = getBorrowableAssets(siloConfig)(silo);
  const [selectedAssetAddress, setSelectedAssetAddress] = useState<ChecksumAddress | undefined>(
    () => assets[0]?.assetAddress
  );
  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.assetAddress === selectedAssetAddress),
    [assets, selectedAssetAddress]
  );

  const assetsToggles = useMemo(
    () =>
      assets.map((asset) => ({
        value: asset.assetAddress,
        label: asset.symbol,
      })),
    [assets]
  );

  useEffect(() => {
    // update selected asset to the first one in a silo, if no selected asset is set
    // or if the currently selected one doesn't belong to the current silo
    if ((assets && !selectedAssetAddress) || !assets.find((asset) => asset.assetAddress === selectedAssetAddress)) {
      setSelectedAssetAddress(assets[0]?.assetAddress);
    }
  }, [assets, selectedAssetAddress]);

  return (
    <ActionPanels
      panels={[
        <ToggleGroup
          id="assets"
          toggles={assetsToggles}
          onChange={setSelectedAssetAddress}
          value={selectedAssetAddress}
          label="Asset"
        />,
        ...(selectedAsset
          ? [
              <Apy siloAddress={silo.siloAddress} asset={selectedAsset} />,
              <Utilization asset={selectedAsset} siloAddress={silo.siloAddress} />,
            ]
          : [<div className="h-56" />]),
      ]}
    />
  );
};

const Apy = ({ asset, siloAddress }: { siloAddress: ChecksumAddress; asset: SiloAsset }) => {
  const [selectedDayRange, setSelectedDayRange] = useState<ChartDateRange>(7);
  const interestRatesQuery = useQueryGraphInterestRates(siloAddress);
  const byAsset = useInterestRatesChartData(interestRatesQuery.data);
  const data = useChartDataForDayRangeAndAsset(byAsset, asset.assetAddress, selectedDayRange);
  const trends = useMemo(() => (data ? calculateTrends(data) : undefined), [data]);

  if (interestRatesQuery.error) return null;

  if (!data) {
    return <PulseApy />;
  }

  return (
    <div className="flex flex-col gap-5">
      <h3 className="text-lg font-medium text-white">{asset.symbol} APR</h3>
      <div className="flex gap-2.5 justify-between">
        <ChartRangeToggle range={selectedDayRange} onChange={setSelectedDayRange} id="charts-panel-day-range" />
        {asset ? (
          <ApyChartLegend
            depositAPY={asPrecisePct(asset.depositAPY)}
            borrowAPY={asPrecisePct(asset.borrowAPY)}
            trend={trends}
            hourly={isHourlyChartDateRange(selectedDayRange)}
          />
        ) : null}
      </div>
      <ApyChart data={data} hourly={isHourlyChartDateRange(selectedDayRange)} />
    </div>
  );
};

const Utilization = ({ asset, siloAddress }: { asset: SiloAsset; siloAddress: ChecksumAddress }) => {
  const query = useQueryInterestRateModelConfig(siloAddress, asset.assetAddress);

  if (!query.data) return <PulseUtilization />;

  const { hasLow, hasOptimal } = getIrmMeta(query.data);

  const chartValues = {
    current: toChartValue(asset.utilization),
    low: toChartValue(query.data.ulow),
    optimal: toChartValue(query.data.uopt),
    critical: toChartValue(query.data.ucrit),
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <h3 className="text-lg font-medium text-white">{asset.symbol} Utilization</h3>
        {asset ? (
          <LegendItem
            value={asWholePct(asset.utilization)}
            label="Current utilization"
            align="center"
            color="bg-white"
          />
        ) : null}
      </div>

      <div className="px-3">
        <UtilizationChart data={chartValues} hasLow={hasLow} hasOptimal={hasOptimal} />
      </div>
    </div>
  );
};

const PulseUtilization = () => {
  return (
    <div className="mt-6 space-y-6 animate-pulse">
      <div className="h-10 rounded-lg bg-shale-750 w-36"></div>
      <div className="rounded-lg bg-shale-750 h-36"></div>
    </div>
  );
};

const PulseApy = () => {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-10 rounded-lg bg-shale-750 w-36"></div>
      <div className="bg-shale-750 rounded-lg h-[270px] w-full"></div>
    </div>
  );
};
